import { when } from '@execonline-inc/maybe-adapter';
import { addDays, isFuture } from 'date-fns';
import { Maybe, isJust } from 'maybeasy';
import {
  ProductLicenseResource,
  ScheduledProgramInstanceOption,
  EitherLearningCollectionStore,
} from '../../../Types';
import LearningDesignExperienceStore from '../../Experience/Store';
import { mapMaybe } from '@execonline-inc/collections';

const checkRenewalDate = (
  endOn: Date,
  daysUntilRenewalDate: number,
  scheduledOption: ScheduledProgramInstanceOption,
): Maybe<ScheduledProgramInstanceOption> =>
  when(() => endOn < addDays(new Date(), daysUntilRenewalDate), scheduledOption);

const scheduledOptionEndsBeforeRenewalDate = (
  productLicense: ProductLicenseResource,
  scheduledOption: ScheduledProgramInstanceOption,
): Maybe<ScheduledProgramInstanceOption> =>
  scheduledOption.endOn.andThen((endOn) =>
    checkRenewalDate(endOn, productLicense.payload.daysUntilRenewal, scheduledOption),
  );

const oneDayAfterProgramStart = (scheduledOption: ScheduledProgramInstanceOption): Date =>
  addDays(scheduledOption.startOn, 1);

const isFutureOption = (
  scheduledOption: ScheduledProgramInstanceOption,
): Maybe<ScheduledProgramInstanceOption> =>
  when(isFuture(oneDayAfterProgramStart(scheduledOption)), scheduledOption);

export const selectableOption = (
  scheduledOption: ScheduledProgramInstanceOption,
  productLicense: ProductLicenseResource,
) =>
  isFutureOption(scheduledOption).andThen((scheduledOption) =>
    scheduledOptionEndsBeforeRenewalDate(productLicense, scheduledOption),
  );

export const selectable = (
  scheduledOption: ScheduledProgramInstanceOption,
  productLicense: ProductLicenseResource,
): boolean => isJust(selectableOption(scheduledOption, productLicense));

export const selectedInLearningCollection = (
  store: EitherLearningCollectionStore,
  scheduledOption: ScheduledProgramInstanceOption,
): Maybe<ScheduledProgramInstanceOption> =>
  when(store.selectedProgramIds.includes(scheduledOption.id), scheduledOption);

export const selectedInExperienceStore = (
  experienceStore: LearningDesignExperienceStore,
  scheduledOption: ScheduledProgramInstanceOption,
): Maybe<ScheduledProgramInstanceOption> =>
  when(experienceStore.selectedIds.includes(scheduledOption.id), scheduledOption);

export const filteredScheduledProgramInstanceOptions = (
  options: ScheduledProgramInstanceOption[],
  productLicense: ProductLicenseResource,
  store: EitherLearningCollectionStore,
): readonly ScheduledProgramInstanceOption[] =>
  mapMaybe(
    (scheduledOption) =>
      selectableOption(scheduledOption, productLicense).orElse(() =>
        selectedInLearningCollection(store, scheduledOption),
      ),
    options,
  );
